import { parseRecovery } from "./recovery.js";
import type { Dict } from "./i18n.js";
import { t as dict } from "./i18n.js";
import { sdkAssetUrl } from "../assets.js";

type QrFn = (t: string, px?: number) => string;

const CSS = `:host{all:initial;font:14px/1.45 Inter,system-ui,sans-serif;color:#e5e7eb}.o{position:fixed;inset:0;background:#020617d9;display:grid;place-items:center;z-index:99999;padding:18px}.b{width:min(100%,30rem);box-sizing:border-box;padding:22px;border:1px solid #2a3143;border-radius:20px;background:linear-gradient(180deg,#18181b,#0b0f17 22%,#0a0d14);box-shadow:0 24px 80px #000a}.b.account{width:min(100%,27rem)}.brand{display:inline-block;margin:0 0 10px;font:700 12px/1.2 ui-monospace,monospace;letter-spacing:.28em;text-transform:uppercase;color:#e5e7eb;text-decoration:none}.brand:hover{color:#fff}.rule{height:1px;background:#232937;margin:0 0 18px}h2{margin:0 0 10px;font:700 18px/1.2 system-ui;color:#fafafa;letter-spacing:.08em;text-transform:uppercase}p{margin:0 0 14px;color:#9ca3af}.hint{max-width:24rem;text-wrap:balance}.warn{color:#fca5a5}.status{display:grid;justify-items:center;gap:14px;padding:16px 0 8px}.frame{position:relative;padding:18px}.frame:before,.frame:after{content:"";position:absolute;inset:0;border:1px solid #2b3547;border-radius:18px;pointer-events:none}.q{display:grid;place-items:center;min-height:132px;padding:18px;background:#f8fafc;border-radius:18px}.q svg{display:block;max-width:100%;height:auto}textarea{width:100%;min-height:4.5rem;font:12px/1.5 ui-monospace,monospace;padding:12px 14px;border:1px solid #334155;background:#0b1120;color:#e2e8f0;box-sizing:border-box;word-break:break-all;border-radius:14px}.r{display:flex;gap:10px;flex-wrap:wrap;margin-top:14px}.r.v{flex-direction:column}.r.v button{width:100%}button{display:inline-flex;align-items:center;justify-content:center;border:1px solid #394254;background:#141922;color:#f3f4f6;padding:.85em 1.15em;font:600 14px/1.2 system-ui;letter-spacing:.12em;text-transform:uppercase;cursor:pointer;border-radius:999px}button.g{background:#f3f4f6;color:#111827}button.d{background:transparent;border-color:#7f1d1d;color:#fca5a5}button:hover:not(:disabled){border-color:#6b7280}button:disabled{opacity:.6;cursor:wait}.e{color:#fca5a5;font-size:12px;margin-top:10px}video{width:100%;max-height:12rem;background:#000;display:none;margin-top:8px;border-radius:14px}video.on{display:block}.hid{display:none}`;

function brandLink() {
  return `<a class="brand" href="https://an.one/" target="_blank" rel="noreferrer">AN.ONE</a>`;
}

function signOutLabel(lang?: string) {
  const l = (lang || "").slice(0, 2).toLowerCase();
  if (l === "ru") return "Выйти";
  if (l === "es") return "Salir";
  return "Sign out";
}

function accountStatusLabel(lang?: string, d?: Dict) {
  const l = (lang || "").slice(0, 2).toLowerCase();
  if (l === "ru") return "Статус аккаунта";
  if (l === "es") return "Estado de la cuenta";
  return d?.accountStatus || "Account status";
}

function accountHintLabel(lang?: string, d?: Dict) {
  const l = (lang || "").slice(0, 2).toLowerCase();
  if (l === "ru") return "Отсканируйте, чтобы подтвердить вход или привязать новое устройство.";
  if (l === "es") return "Escanea para verificar tu identidad o establecer la linea de confianza.";
  return d?.accountHint || "Scan to verify identity or establish trust line.";
}

function lossWarning(lang?: string) {
  const l = (lang || "").slice(0, 2).toLowerCase();
  if (l === "ru") return "Если потеряете приватный ключ, восстановить аккаунт будет невозможно.";
  if (l === "es") return "Si pierdes la clave privada, no podras recuperar la cuenta.";
  return "If you lose the private key, this account cannot be restored.";
}

function qrUrl() {
  return sdkAssetUrl("dwtf.qr.js");
}

export function loadQr(): Promise<QrFn> {
  const g = globalThis as unknown as { DWTF_QR?: { qrSvg: QrFn } };
  if (g.DWTF_QR?.qrSvg) return Promise.resolve(g.DWTF_QR.qrSvg);
  return new Promise((ok, bad) => {
    const s = document.createElement("script");
    s.src = qrUrl();
    s.onload = () => (g.DWTF_QR?.qrSvg ? ok(g.DWTF_QR.qrSvg) : bad(new Error("QR")));
    s.onerror = () => bad(new Error("QR"));
    document.head.append(s);
  });
}

function shadow() {
  const el = document.createElement("div");
  el.dataset.dwtf = "m";
  const root = el.attachShadow({ mode: "open" });
  root.innerHTML = `<style>${CSS}</style>`;
  document.body.append(el);
  return { root, close: () => el.remove() };
}

function copyText(text: string, btn: HTMLElement, label: string) {
  const p = navigator.clipboard?.writeText(text);
  const done = () => {
    btn.textContent = label;
  };
  if (p) void p.then(done);
  else {
    try {
      document.execCommand("copy");
      done();
    } catch {
      /* */
    }
  }
}

export type BackupOpts = { recoveryKey: string; lang?: string; onClose?: () => void };

export function openBackupModal(opts: BackupOpts) {
  const d = dict(opts.lang);
  const { root, close } = shadow();
  const box = document.createElement("div");
  box.className = "o";
  box.innerHTML = `<div class="b" role="dialog">${brandLink()}<div class="rule"></div><h2>${d.saveKey}</h2><p>${d.saveHint}</p><p class="warn">${lossWarning(opts.lang)}</p><textarea class="hid" readonly></textarea><div class="status"><div class="q"></div></div><div class="r"><button data-x="v">${d.reveal}</button><button data-x="c">${d.copy}</button><button class="g" data-x="ok">${d.done}</button></div></div>`;
  const ta = box.querySelector("textarea")!;
  ta.value = opts.recoveryKey;
  const q = box.querySelector(".q")!;
  void loadQr()
    .then((qr) => {
      q.innerHTML = qr(opts.recoveryKey);
    })
    .catch(() => {
      q.textContent = "QR n/a";
    });
  const done = () => {
    close();
    opts.onClose?.();
  };
  box.onclick = (e) => {
    const t = e.target as HTMLElement;
    if (t === box || t.dataset.x === "ok") done();
    if (t.dataset.x === "v") {
      ta.classList.toggle("hid");
      t.textContent = ta.classList.contains("hid") ? d.reveal : d.hide;
    }
    if (t.dataset.x === "c") copyText(opts.recoveryKey, t, d.copied);
  };
  root.append(box);
  return done;
}

export type RestoreOpts = {
  onRestore: (k: string) => void | Promise<void>;
  lang?: string;
  onClose?: () => void;
};

export type ChoiceOpts = {
  onCreate: () => void | Promise<void>;
  onRestore: () => void | Promise<void>;
  lang?: string;
  onClose?: () => void;
};

export function openChoiceModal(opts: ChoiceOpts) {
  const d = dict(opts.lang);
  const { root, close } = shadow();
  const box = document.createElement("div");
  box.className = "o";
  box.innerHTML = `<div class="b" role="dialog">${brandLink()}<div class="rule"></div><h2>${d.brand}</h2><p class="hint">${d.noKey}</p><p class="warn">${lossWarning(opts.lang)}</p><div class="r v"><button class="g" data-x="n">${d.create}</button><button data-x="k">${d.restore}</button><button data-x="x">${d.close}</button></div></div>`;
  const done = () => {
    close();
    opts.onClose?.();
  };
  box.onclick = (e) => {
    const t = e.target as HTMLElement;
    if (t === box || t.dataset.x === "x") done();
    if (t.dataset.x === "n") {
      close();
      void opts.onCreate();
    }
    if (t.dataset.x === "k") {
      close();
      void opts.onRestore();
    }
  };
  root.append(box);
  return done;
}

export function openRestoreModal(opts: RestoreOpts) {
  const d = dict(opts.lang);
  const { root, close } = shadow();
  const box = document.createElement("div");
  box.className = "o";
  box.innerHTML = `<div class="b" role="dialog">${brandLink()}<div class="rule"></div><h2>${d.restore}</h2><p>${d.paste}</p><p class="warn">${lossWarning(opts.lang)}</p><textarea placeholder="d1…"></textarea><video playsinline muted></video><div class="e" hidden></div><div class="r"><button class="g" data-x="go">${d.cont}</button><button data-x="sc">${d.qr}</button><button data-x="x">${d.close}</button></div></div>`;
  const ta = box.querySelector("textarea")!;
  const err = box.querySelector(".e") as HTMLElement;
  const video = box.querySelector("video") as HTMLVideoElement;
  let stream: MediaStream | null = null,
    raf = 0;
  const stop = () => {
    cancelAnimationFrame(raf);
    stream?.getTracks().forEach((t) => t.stop());
    stream = null;
    video.classList.remove("on");
  };
  const finish = () => {
    stop();
    close();
    opts.onClose?.();
  };
  const fail = (m: string) => {
    err.hidden = false;
    err.textContent = m;
  };
  const go = async (raw: string) => {
    try {
      parseRecovery(raw);
      await opts.onRestore(raw.trim());
      finish();
    } catch (e) {
      fail(e instanceof Error ? e.message : "err");
    }
  };
  const scan = async () => {
    err.hidden = true;
    const BD = (
      window as unknown as {
        BarcodeDetector?: new (o: { formats: string[] }) => {
          detect(s: ImageBitmapSource): Promise<{ rawValue: string }[]>;
        };
      }
    ).BarcodeDetector;
    if (!BD) return fail(d.paste);
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false,
      });
      video.srcObject = stream;
      video.classList.add("on");
      await video.play();
      const det = new BD({ formats: ["qr_code"] });
      const tick = async () => {
        if (!stream) return;
        try {
          const v = (await det.detect(video))[0]?.rawValue;
          if (v) {
            ta.value = v;
            stop();
            await go(v);
            return;
          }
        } catch {
          /* */
        }
        raf = requestAnimationFrame(() => void tick());
      };
      raf = requestAnimationFrame(() => void tick());
    } catch {
      fail(d.paste);
    }
  };
  box.onclick = (e) => {
    const t = e.target as HTMLElement;
    if (t === box || t.dataset.x === "x") finish();
    if (t.dataset.x === "go") void go(ta.value);
    if (t.dataset.x === "sc") void scan();
  };
  root.append(box);
  return finish;
}

export type AccountOpts = {
  recoveryKey: string;
  lang?: string;
  onImport: () => void;
  onLogout: () => void | Promise<void>;
  onClose?: () => void;
};

export function openAccountModal(opts: AccountOpts) {
  const d = dict(opts.lang);
  const { root, close } = shadow();
  const box = document.createElement("div");
  box.className = "o";
  box.innerHTML = `<div class="b account" role="dialog">${brandLink()}<div class="rule"></div><h2>${accountStatusLabel(opts.lang, d)}</h2><p class="hint">${accountHintLabel(opts.lang, d)}</p><p class="warn">${lossWarning(opts.lang)}</p><textarea class="hid" readonly></textarea><div class="status"><div class="frame"><div class="q"></div></div></div><div class="r v"><button class="g" data-x="v">${d.reveal}</button><button data-x="c">${d.copy}</button><button data-x="i">${d.restore}</button><button class="d" data-x="l">${signOutLabel(opts.lang)}</button><button data-x="x">${d.close}</button></div></div>`;
  const ta = box.querySelector("textarea")!;
  ta.value = opts.recoveryKey;
  const q = box.querySelector(".q")!;
  void loadQr()
    .then((qr) => {
      q.innerHTML = qr(opts.recoveryKey);
    })
    .catch(() => {
      /* */
    });
  const done = () => {
    close();
    opts.onClose?.();
  };
  box.onclick = (e) => {
    const t = e.target as HTMLElement;
    if (t === box || t.dataset.x === "x") done();
    if (t.dataset.x === "v") {
      ta.classList.toggle("hid");
      t.textContent = ta.classList.contains("hid") ? d.reveal : d.hide;
    }
    if (t.dataset.x === "c") copyText(opts.recoveryKey, t, d.copied);
    if (t.dataset.x === "i") {
      close();
      opts.onImport();
    }
    if (t.dataset.x === "l") void Promise.resolve(opts.onLogout()).then(done);
  };
  root.append(box);
  return done;
}

export type { Dict };
